/**
 * The working context's provider, the header band that shows it, and the
 * panel that changes it. The state and its rules are `workspace-context.ts`.
 *
 * The band is always visible and always complete: every field is named even
 * when it is "all", because "Toutes sociétés" on screen is an answer and an
 * absent field is a question.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import { useDebounced, useResource } from "@hardauto/core-data";
import { Button, Dialog, Input, Select } from "@hardauto/core-ui";
import {
  EMPTY_STRUCTURE,
  citiesOf,
  narrowStructure,
  type StockStructure,
} from "@hardauto/feature-catalog";
import { ClientsApi } from "@hardauto/feature-clients";
import { useSession } from "@hardauto/feature-iam";

import { client } from "./api";
import { loadStockStructure, useStructureVersion } from "./structure";
import {
  WorkspaceContext,
  browserStorage,
  emptyWorkspace,
  readWorkspace,
  useWorkspace,
  writeWorkspace,
  type Workspace,
  type WorkspaceValue,
} from "./workspace-context";
import "./i18n";

/** The saved context, read from what the server returned the way storage is read. */
function fromServer(saved: unknown): Workspace {
  return readWorkspace({ getItem: () => JSON.stringify(saved) });
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspace, setWorkspace] = useState<Workspace>(() => readWorkspace(browserStorage()));
  const [panelOpen, setPanelOpen] = useState(false);
  const session = useSession();
  const authenticated = session.status === "authenticated";
  // True once this sign-in's saved context has been read: until then, a change
  // must not be written back, or the empty start would erase what is saved.
  const [loaded, setLoaded] = useState(false);

  // The browser's copy, for the next page load before the server answers.
  useEffect(() => {
    writeWorkspace(browserStorage(), workspace);
  }, [workspace]);

  // The user's own context, kept on the server: the same after a refresh, a
  // sign-out and a sign-in, on any browser.
  useEffect(() => {
    if (!authenticated) {
      setLoaded(false);
      return;
    }
    let cancelled = false;
    client
      .request<{ workspace: Record<string, unknown> | null }>(
        client.authenticatedPath("/me/workspace"),
      )
      .then((saved) => {
        if (cancelled) return;
        // Nothing saved yet: keep what this browser has, and save it from now on.
        if (saved.workspace) setWorkspace(fromServer(saved.workspace));
        setLoaded(true);
      })
      .catch(() => {
        // The server could not be asked: the browser's copy still works. Not
        // writing back, so a bad moment cannot erase the saved one.
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  const debounced = useDebounced(workspace, 600);
  useEffect(() => {
    if (!authenticated || !loaded) return;
    void client
      .request(client.authenticatedPath("/me/workspace"), {
        method: "PUT",
        body: { workspace: debounced },
      })
      .catch(() => undefined);
  }, [debounced, authenticated, loaded]);

  const value = useMemo<WorkspaceValue>(
    () => ({
      workspace,
      update: (patch) => {
        setWorkspace((current) => ({ ...current, ...patch }));
      },
      reset: () => {
        setWorkspace(emptyWorkspace());
      },
      panelOpen,
      setPanelOpen,
    }),
    [workspace, panelOpen],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/**
 * The structure, when this principal may read it; empty otherwise. `loaded`
 * is false until a read succeeded, so an empty structure is never mistaken
 * for one in which the context's company no longer exists.
 */
function useStructure(): { structure: StockStructure; loaded: boolean } {
  const session = useSession();
  const mayRead = useCan("referential.site.read") && session.status === "authenticated";
  const fetcher = useCallback(
    () => (mayRead ? loadStockStructure() : Promise.resolve(EMPTY_STRUCTURE)),
    [mayRead],
  );
  const resource = useResource(fetcher);
  // Read again after every edit on the structure screen: the band outlives it.
  const version = useStructureVersion();
  const readAt = useRef(version);
  const { reload } = resource;
  useEffect(() => {
    if (readAt.current === version) return;
    readAt.current = version;
    reload();
  }, [version, reload]);
  return {
    structure: resource.data ?? EMPTY_STRUCTURE,
    loaded: mayRead && resource.data !== null && !resource.refreshing && !resource.failed,
  };
}

/**
 * Drop from the context a company, point of sale or depot the structure no
 * longer offers — hidden or deleted since it was chosen. Kept, it would
 * scope every list to nothing while the band still named it.
 */
function useForgetRetired(structure: StockStructure, loaded: boolean) {
  const { workspace, update } = useWorkspace();
  useEffect(() => {
    if (!loaded) return;
    const patch: Partial<Workspace> = {};
    if (
      workspace.companyCode &&
      !structure.companies.some((entry) => entry.code === workspace.companyCode)
    ) {
      patch.companyCode = "";
    }
    if (
      workspace.pointOfSaleCode &&
      !structure.pointsOfSale.some((entry) => entry.code === workspace.pointOfSaleCode)
    ) {
      patch.pointOfSaleCode = "";
    }
    if (workspace.siteCode && !structure.sites.some((entry) => entry.code === workspace.siteCode)) {
      patch.siteCode = "";
    }
    if (Object.keys(patch).length > 0) update(patch);
  }, [
    loaded,
    structure,
    workspace.companyCode,
    workspace.pointOfSaleCode,
    workspace.siteCode,
    update,
  ]);
}

function monthLabel(period: string, locale: string): string {
  const [year, month] = period.split("-").map(Number);
  if (!year || !month) return period;
  const label = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(
    new Date(year, month - 1, 1),
  );
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** The band under the top bar: the context, in full, and the way to change it. */
export function ContextBar() {
  const { t, i18n } = useTranslation("backoffice");
  const { workspace, setPanelOpen } = useWorkspace();
  const { structure, loaded } = useStructure();
  useForgetRetired(structure, loaded);

  const company = structure.companies.find((entry) => entry.code === workspace.companyCode);
  const point = structure.pointsOfSale.find((entry) => entry.code === workspace.pointOfSaleCode);
  const site = structure.sites.find((entry) => entry.code === workspace.siteCode);

  const items: { key: string; label: string; value: string }[] = [
    {
      key: "client",
      label: t("context.client"),
      value: workspace.client?.name || workspace.client?.code || t("context.noClient"),
    },
    {
      key: "company",
      label: t("context.company"),
      value: company?.name ?? (workspace.companyCode || t("context.allCompanies")),
    },
    {
      key: "city",
      label: t("context.city"),
      value: workspace.city || t("context.allCities"),
    },
    {
      key: "point",
      label: t("context.pointOfSale"),
      value: point?.name ?? (workspace.pointOfSaleCode || t("context.allPointsOfSale")),
    },
    {
      key: "depot",
      label: t("context.depot"),
      value: site?.name ?? (workspace.siteCode || t("context.allDepots")),
    },
    {
      key: "active",
      label: t("context.active"),
      value: t(`context.active_${workspace.active}`),
    },
    {
      key: "period",
      label: t("context.period"),
      value: monthLabel(workspace.period, i18n.language),
    },
  ];

  return (
    <section className="hardauto-context" aria-label={t("context.label")}>
      <dl className="hardauto-context__items">
        {items.map((item) => (
          <div key={item.key} className="hardauto-context__item">
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
      <Button
        size="sm"
        onClick={() => {
          setPanelOpen(true);
        }}
      >
        {t("context.change")}
      </Button>
      <ContextPanel structure={structure} />
    </section>
  );
}

/**
 * The side panel: a draft of the context, applied in one go.
 *
 * A draft rather than live edits, because the levels depend on each other —
 * choosing a company empties the depot when the depot was another
 * company's — and applying each step would reload every list on screen four
 * times for one change of mind.
 */
function ContextPanel({ structure }: { structure: StockStructure }) {
  const { t } = useTranslation("backoffice");
  const { workspace, update, panelOpen, setPanelOpen } = useWorkspace();
  const mayReadClients = useCan("clients.client.read");
  const mayReadStructure = useCan("referential.site.read");

  const [draft, setDraft] = useState<Workspace>(workspace);
  const [openedWith, setOpenedWith] = useState(false);
  // Re-read the context each time the panel opens, during render rather than
  // in an effect, so the first painted frame already shows the current one.
  if (panelOpen && !openedWith) {
    setOpenedWith(true);
    setDraft(workspace);
  } else if (!panelOpen && openedWith) {
    setOpenedWith(false);
  }

  const narrowed = narrowStructure(structure, draft);
  const change = (patch: Partial<Workspace>) => {
    setDraft((current) => {
      const next = { ...current, ...patch };
      // A narrower level that no longer fits the wider ones is cleared, so
      // the context never names a depot of another company.
      const fits = narrowStructure(structure, next);
      if (next.pointOfSaleCode && !fits.pointsOfSale.some((p) => p.code === next.pointOfSaleCode)) {
        next.pointOfSaleCode = "";
      }
      const sites = narrowStructure(structure, next).sites;
      if (next.siteCode && !sites.some((s) => s.code === next.siteCode)) next.siteCode = "";
      return next;
    });
  };

  const close = () => {
    setPanelOpen(false);
  };

  return (
    <Dialog
      open={panelOpen}
      onClose={close}
      placement="side"
      title={t("context.panelTitle")}
      description={t("context.panelDescription")}
      closeLabel={t("context.close")}
      footer={
        <>
          <Button
            onClick={() => {
              setDraft({ ...emptyWorkspace(), period: draft.period });
            }}
          >
            {t("context.reset")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              update(draft);
              close();
            }}
          >
            {t("context.apply")}
          </Button>
        </>
      }
    >
      <div className="hardauto-context-panel">
        {mayReadClients ? (
          <ClientPicker
            value={draft.client}
            onChange={(picked) => {
              change({ client: picked });
            }}
          />
        ) : null}

        {mayReadStructure ? (
          <>
            <PanelField label={t("context.company")}>
              {(id) => (
                <Select
                  id={id}
                  value={draft.companyCode}
                  onChange={(event) => change({ companyCode: event.target.value })}
                >
                  <option value="">{t("context.allCompanies")}</option>
                  {structure.companies.map((company) => (
                    <option key={company.code} value={company.code}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              )}
            </PanelField>
            <PanelField label={t("context.city")}>
              {(id) => (
                <Select
                  id={id}
                  value={draft.city}
                  onChange={(event) => change({ city: event.target.value })}
                >
                  <option value="">{t("context.allCities")}</option>
                  {citiesOf(structure).map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </Select>
              )}
            </PanelField>
            <PanelField label={t("context.pointOfSale")}>
              {(id) => (
                <Select
                  id={id}
                  value={draft.pointOfSaleCode}
                  onChange={(event) => change({ pointOfSaleCode: event.target.value })}
                >
                  <option value="">{t("context.allPointsOfSale")}</option>
                  {narrowed.pointsOfSale.map((point) => (
                    <option key={point.code} value={point.code}>
                      {point.name}
                    </option>
                  ))}
                </Select>
              )}
            </PanelField>
            <PanelField label={t("context.depot")}>
              {(id) => (
                <Select
                  id={id}
                  value={draft.siteCode}
                  onChange={(event) => change({ siteCode: event.target.value })}
                >
                  <option value="">{t("context.allDepots")}</option>
                  {narrowed.sites.map((site) => (
                    <option key={site.code} value={site.code}>
                      {site.name}
                    </option>
                  ))}
                </Select>
              )}
            </PanelField>
          </>
        ) : null}

        <PanelField label={t("context.active")}>
          {(id) => (
            <Select
              id={id}
              value={draft.active}
              onChange={(event) => change({ active: event.target.value as Workspace["active"] })}
            >
              <option value="all">{t("context.active_all")}</option>
              <option value="active">{t("context.active_active")}</option>
              <option value="inactive">{t("context.active_inactive")}</option>
            </Select>
          )}
        </PanelField>

        <PanelField label={t("context.period")}>
          {(id) => (
            <Input
              id={id}
              type="month"
              value={draft.period}
              onChange={(event) => {
                if (event.target.value) change({ period: event.target.value });
              }}
            />
          )}
        </PanelField>
      </div>
    </Dialog>
  );
}

let fieldCounter = 0;

function PanelField({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const [id] = useState(() => `hardauto-context-${String((fieldCounter += 1))}`);
  return (
    <div className="hardauto-context-panel__field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
    </div>
  );
}

/** Search a customer by code or name; pick one, or none. */
function ClientPicker({
  value,
  onChange,
}: {
  value: Workspace["client"];
  onChange: (client: Workspace["client"]) => void;
}) {
  const { t } = useTranslation("backoffice");
  const api = useMemo(() => new ClientsApi(client), []);
  const [search, setSearch] = useState("");
  const term = useDebounced(search, 250);
  const fetcher = useCallback(
    () =>
      term.trim()
        ? api.listClients({ search: term.trim(), limit: 8 })
        : Promise.resolve({ items: [], total: 0, next_cursor: null }),
    [api, term],
  );
  const results = useResource(fetcher);

  return (
    <div className="hardauto-context-panel__field">
      <PanelField label={t("context.client")}>
        {(id) => (
          <Input
            id={id}
            type="search"
            placeholder={t("context.clientSearch")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        )}
      </PanelField>
      <p className="hardauto-context-panel__current">
        {value ? (
          <>
            <strong>{value.name || value.code}</strong> <code>{value.code}</code>{" "}
            <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
              {t("context.clearClient")}
            </Button>
          </>
        ) : (
          t("context.noClient")
        )}
      </p>
      {(results.data?.items ?? []).length > 0 ? (
        <ul className="hardauto-context-panel__results" aria-label={t("context.clientResults")}>
          {(results.data?.items ?? []).map((row) => (
            <li key={row.client.id}>
              <button
                type="button"
                className="hardauto-context-panel__result"
                onClick={() => {
                  onChange({
                    id: row.client.id,
                    code: row.client.code,
                    name: row.client.company_name,
                  });
                  setSearch("");
                }}
              >
                <span>{row.client.company_name}</span>
                <code>{row.client.code}</code>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
